import React, { useCallback, useEffect, useRef, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// CameraCapture — photo capture for the Contract Workers module. Uses
// getUserMedia + canvas for a live shot, and (when allowGallery) also lets the
// user pick an existing image from their device. Calls onCapture(File) with the
// chosen JPEG/image File, or onCapture(null) when the shot is cleared.
// ─────────────────────────────────────────────────────────────────────────────

// facing: "environment" (rear camera, default — group/worker shots taken by a
// supervisor) or "user" (front camera — a worker's own selfie).
const CameraCapture = ({
  onCapture,
  disabled,
  allowGallery = true,
  facing = "environment",
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [shotUrl, setShotUrl] = useState(null);
  const [error, setError] = useState("");

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  // Always release the camera when the component goes away.
  useEffect(() => stopStream, [stopStream]);

  const openCamera = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This device/browser has no camera access.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing } },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true);
      // videoRef is rendered once `streaming` is true — wait a tick.
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 0);
    } catch (err) {
      setError(
        err?.name === "NotAllowedError"
          ? "Camera permission denied. Allow it in the browser and retry."
          : "Could not open the camera.",
      );
      stopStream();
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 960;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `${facing === "user" ? "selfie" : "worker"}_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        if (shotUrl) URL.revokeObjectURL(shotUrl);
        setShotUrl(URL.createObjectURL(file));
        onCapture(file);
        stopStream();
      },
      "image/jpeg",
      0.9,
    );
  };

  const retake = () => {
    if (shotUrl) URL.revokeObjectURL(shotUrl);
    setShotUrl(null);
    onCapture(null);
    openCamera();
  };

  const clearShot = () => {
    if (shotUrl) URL.revokeObjectURL(shotUrl);
    setShotUrl(null);
    onCapture(null);
  };

  const onPickFile = (e) => {
    setError("");
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    stopStream();
    if (shotUrl) URL.revokeObjectURL(shotUrl);
    setShotUrl(URL.createObjectURL(file));
    onCapture(file);
  };

  return (
    <div>
      {!streaming && !shotUrl && (
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            disabled={disabled}
            onClick={openCamera}
          >
            <i className="fa fa-camera me-1" /> Open Camera
          </button>
          {allowGallery && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={disabled}
              onClick={() => fileRef.current?.click()}
            >
              <i className="fa fa-image me-1" /> Upload from Gallery
            </button>
          )}
        </div>
      )}

      {allowGallery && (
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={onPickFile}
        />
      )}

      {streaming && (
        <div>
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              width: "100%",
              maxWidth: 320,
              borderRadius: 10,
              background: "#000",
              display: "block",
            }}
          />
          <div className="mt-2 d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={capture}
            >
              <i className="fa fa-circle me-1" /> Capture
            </button>
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={stopStream}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {shotUrl && (
        <div>
          <img
            src={shotUrl}
            alt="captured"
            style={{
              width: "100%",
              maxWidth: 320,
              borderRadius: 10,
              objectFit: "cover",
              display: "block",
            }}
          />
          <div className="mt-2 d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={disabled}
              onClick={retake}
            >
              <i className="fa fa-redo me-1" /> Retake
            </button>
            {allowGallery && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={disabled}
                onClick={() => fileRef.current?.click()}
              >
                <i className="fa fa-image me-1" /> Gallery
              </button>
            )}
            <button
              type="button"
              className="btn btn-light btn-sm"
              disabled={disabled}
              onClick={clearShot}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {error && <small className="text-danger d-block mt-1">{error}</small>}
    </div>
  );
};

export default CameraCapture;
